import React, { useState } from "react";
import {
  Card,
  Row,
  Col,
  Typography,
  Divider,
  Button,
  Tag,
  Tooltip,
  Table,
  Collapse,
  Alert,
  message,
  Skeleton,
} from "antd";
import {
  ReloadOutlined,
  InfoCircleOutlined,
  ExportOutlined,
  CreditCardOutlined,
  CopyOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";

const { Text, Title } = Typography;

const formatMoney = (val, currency = "AED") => {
  if (val === null || val === undefined || isNaN(Number(val))) {
    return `0.00 ${currency}`;
  }
  return `${Number(val).toFixed(2)} ${currency}`;
};

const OrderSummaryCard = ({
  orderSummary,
  loading = false,
  onRefresh,
  isDarkMode = false,
  style = {},
}) => {
  const { t } = useTranslation();
  const [activeCollapse, setActiveCollapse] = useState([]);

  if (loading && !orderSummary) {
    return (
      <Card
        style={{
          borderRadius: 12,
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
          marginBottom: 20,
          background: isDarkMode ? "#1f1f1f" : "#ffffff",
          borderColor: isDarkMode ? "#303030" : "#f0f0f0",
          ...style,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: 16,
          }}
        >
          <Skeleton.Input active size="small" style={{ width: 160 }} />
          <Skeleton.Button active size="small" style={{ width: 60 }} />
        </div>
        <Row gutter={[24, 16]}>
          <Col xs={24} sm={12}>
            <Skeleton
              active
              paragraph={{ rows: 3, width: ["100%", "90%", "80%"] }}
              title={false}
            />
          </Col>
          <Col xs={24} sm={12}>
            <Skeleton
              active
              paragraph={{ rows: 3, width: ["100%", "90%", "80%"] }}
              title={false}
            />
          </Col>
        </Row>
        <div style={{ marginTop: 16 }}>
          <Skeleton
            active
            paragraph={{ rows: 1, width: "60%" }}
            title={{ width: "30%" }}
          />
        </div>
      </Card>
    );
  }

  if (!orderSummary && !loading) {
    return (
      <Card
        title={<span style={{ fontWeight: 700 }}>{t("orderDetails.summaryTitle", "Order summary")}</span>}
        extra={
          <Button
            type="text"
            icon={<ReloadOutlined />}
            onClick={onRefresh}
            style={{ color: "#1677ff" }}
          >
            {t("orderDetails.refresh", "Refresh")}
          </Button>
        }
        style={{
          borderRadius: 12,
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
          marginBottom: 20,
        }}
      >
        <Alert
          type="info"
          showIcon
          message={t("orderDetails.noFinancialSummary", "No financial summary available for this order.")}
        />
      </Card>
    );
  }

  const {
    order_amount,
    coupon_discount_amount,
    points_used,
    final_total,
    vat,
    tax,
    order_1688_price,
    order_shipping_fee,
    coupon,
    estimated_shipping_cost,
    transactions = [],
    profit,
  } = orderSummary || {};

  const estimatedShippingText =
    estimated_shipping_cost?.amount !== null &&
    estimated_shipping_cost?.amount !== undefined
      ? formatMoney(
          estimated_shipping_cost.amount,
          estimated_shipping_cost.currency || "AED"
        )
      : null;

  const transactionColumns = [
    {
      title: t("orderDetails.transactionDate", "Date"),
      dataIndex: "payment_created_date",
      key: "payment_created_date",
      render: (text) =>
        text ? new Date(text).toLocaleString() : "-",
    },
    {
      title: t("orderDetails.transactionType", "Type"),
      dataIndex: "payment_type",
      key: "payment_type",
      render: (type) => (
        <Tag
          color={
            String(type).toLowerCase().includes("refund")
              ? "orange"
              : "green"
          }
        >
          {type || "Pay"}
        </Tag>
      ),
    },
    {
      title: t("orderDetails.transactionAmount", "Amount"),
      key: "amount",
      render: (_, r) => (
        <span style={{ fontWeight: 600 }}>
          {formatMoney(r.amount, r.currency || "AED")}
        </span>
      ),
    },
    {
      title: t("orderDetails.transactionMethod", "Method"),
      dataIndex: "payment_method",
      key: "payment_method",
      render: (method) => (
        <Tag color="blue">{String(method || "-").toUpperCase()}</Tag>
      ),
    },
    {
      title: t("orderDetails.paymentId", "Payment ID"),
      dataIndex: "payment_id",
      key: "payment_id",
      render: (paymentId) =>
        paymentId ? (
          <Tooltip title={`Full ID: ${paymentId}`}>
            <span
              style={{
                fontFamily: "monospace",
                fontSize: 12,
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
              }}
              onClick={() => {
                navigator.clipboard.writeText(paymentId);
                message.success(t("orderDetails.paymentIdCopied", "Payment ID copied!"));
              }}
            >
              {paymentId.slice(0, 10)}...
              <CopyOutlined style={{ color: "#1677ff", fontSize: 11 }} />
            </span>
          </Tooltip>
        ) : (
          "-"
        ),
    },
    {
      title: t("orderDetails.status", "Status"),
      dataIndex: "status",
      key: "status",
      render: (status) => {
        const s = String(status || "").toLowerCase();
        let color = "default";
        if (s.includes("success") || s.includes("captured") || s.includes("paid"))
          color = "success";
        else if (s.includes("fail") || s.includes("error")) color = "error";
        else if (s.includes("pending")) color = "warning";
        return <Tag color={color}>{status || "-"}</Tag>;
      },
    },
    {
      title: t("table.columns.paymentStatus", "Paid"),
      dataIndex: "paid",
      key: "paid",
      render: (paid) => (
        <Tag color={paid ? "green" : "default"}>
          {paid ? t("orderDetails.yes", "Yes") : t("orderDetails.no", "No")}
        </Tag>
      ),
    },
    {
      title: t("orderDetails.captured", "Captured"),
      dataIndex: "captured",
      key: "captured",
      render: (captured) => (
        <Tag color={captured ? "cyan" : "default"}>
          {captured ? t("orderDetails.yes", "Yes") : t("orderDetails.no", "No")}
        </Tag>
      ),
    },
    {
      title: t("orderDetails.receipt", "Receipt"),
      dataIndex: "receipt_url",
      key: "receipt_url",
      render: (url) =>
        url ? (
          <Button
            type="link"
            size="small"
            icon={<ExportOutlined />}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t("orderDetails.receipt", "Receipt")}
          </Button>
        ) : (
          "-"
        ),
    },
  ];

  return (
    <Card
      title={
        <Title level={5} style={{ margin: 0, fontWeight: 700 }}>
          {t("orderDetails.summaryTitle", "Order summary")}
        </Title>
      }
      extra={
        <Button
          type="text"
          icon={<ReloadOutlined spin={loading} />}
          onClick={onRefresh}
          loading={loading}
          style={{ color: "#1677ff", fontWeight: 500 }}
        >
          {t("orderDetails.refresh", "Refresh")}
        </Button>
      }
      style={{
        borderRadius: 12,
        boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
        marginBottom: 20,
        border: "1px solid #e8e8e8",
        ...style,
      }}
      bodyStyle={{ padding: "20px 24px" }}
    >
      <Row gutter={[48, 12]}>
        {/* Left Column: Customer Payments / Totals */}
        <Col xs={24} md={12}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 12,
            }}
          >
            <Text type="secondary" style={{ fontSize: 14 }}>
              {t("orderDetails.orderAmount", "Order amount")}
            </Text>
            <Text strong style={{ fontSize: 14 }}>
              {formatMoney(order_amount)}
            </Text>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 12,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <Text type="secondary" style={{ fontSize: 14 }}>
                {t("orderDetails.couponDiscount", "Coupon discount")}
              </Text>
              {coupon?.code && (
                <Tag color="cyan" style={{ margin: 0, fontSize: 11 }}>
                  {coupon.code}
                </Tag>
              )}
            </div>
            <Text strong style={{ fontSize: 14 }}>
              {formatMoney(coupon_discount_amount)}
            </Text>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 12,
            }}
          >
            <Text type="secondary" style={{ fontSize: 14 }}>
              {t("orderDetails.pointsUsed", "Points used")}
            </Text>
            <Text strong style={{ fontSize: 14 }}>
              {Number(points_used || 0)}
            </Text>
          </div>

          <Divider style={{ margin: "14px 0" }} />

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Text strong style={{ fontSize: 15 }}>
              {t("orderDetails.finalTotal", "Final total")}
            </Text>
            <Text
              strong
              style={{
                fontSize: 16,
                color: "#1677ff",
                fontWeight: 700,
              }}
            >
              {formatMoney(final_total)}
            </Text>
          </div>
        </Col>

        {/* Right Column: Taxes & Fulfillment Costs */}
        <Col xs={24} md={12}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 12,
            }}
          >
            <Text type="secondary" style={{ fontSize: 14 }}>
              {t("orderDetails.vat", "VAT")}
            </Text>
            <Text strong style={{ fontSize: 14 }}>
              {formatMoney(vat)}
            </Text>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 12,
            }}
          >
            <Text type="secondary" style={{ fontSize: 14 }}>
              {t("orderDetails.tax", "Tax")}
            </Text>
            <Text strong style={{ fontSize: 14 }}>
              {formatMoney(tax)}
            </Text>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 12,
            }}
          >
            <Text type="secondary" style={{ fontSize: 14 }}>
              {t("orderDetails.cost1688", "1688 cost")}
            </Text>
            <Text strong style={{ fontSize: 14 }}>
              {formatMoney(order_1688_price)}
            </Text>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 12,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <Text type="secondary" style={{ fontSize: 14 }}>
                {t("orderDetails.shippingFee", "Shipping fee")}
              </Text>
              <Tooltip title={t("orderDetails.shippingFeeTooltip", "Shipping charge paid by the customer (included in Final total)")}>
                <InfoCircleOutlined
                  style={{ fontSize: 12, color: "#8c8c8c" }}
                />
              </Tooltip>
            </div>
            <Text strong style={{ fontSize: 14 }}>
              {formatMoney(order_shipping_fee)}
            </Text>
          </div>

          {estimatedShippingText && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 6,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <Text type="secondary" style={{ fontSize: 13 }}>
                  {t("orderDetails.estimatedShippingAramex", "Estimated shipping (Aramex)")}
                </Text>
                <Tooltip title={t("orderDetails.estimatedShippingTooltip", "Aramex rate-calculator estimate across shipments")}>
                  <InfoCircleOutlined
                    style={{ fontSize: 12, color: "#8c8c8c" }}
                  />
                </Tooltip>
              </div>
              <Text style={{ fontSize: 13, color: "#595959" }}>
                {estimatedShippingText}
              </Text>
            </div>
          )}
        </Col>
      </Row>

      {/* Profit Banner */}
      <div
        style={{
          background: isDarkMode ? "#13231b" : "#eefaf2",
          border: isDarkMode ? "1px solid #1f472e" : "1px solid #d3f4db",
          borderRadius: 8,
          padding: "14px 20px",
          marginTop: 18,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div>
          <div
            style={{
              color: "#278248",
              fontWeight: 700,
              fontSize: 15,
            }}
          >
            {t("orderDetails.profit", "Profit")}
          </div>
          <div
            style={{
              color: isDarkMode ? "#73c991" : "#52986c",
              fontSize: 12,
              marginTop: 2,
            }}
          >
            {t("orderDetails.profitFormula", "Final total – VAT – tax – 1688 cost – shipping cost")}
          </div>
        </div>
        <div>
          {profit !== null && profit !== undefined ? (
            <span
              style={{
                color: "#278248",
                fontWeight: 700,
                fontSize: 24,
              }}
            >
              {Number(profit).toFixed(2)} AED
            </span>
          ) : (
            <Tooltip title={t("orderDetails.profitPendingTooltip", "Profit cannot be computed until Aramex estimated shipping cost is calculated")}>
              <span
                style={{
                  color: "#8c8c8c",
                  fontSize: 14,
                  fontStyle: "italic",
                }}
              >
                {t("orderDetails.profitPending", "Not available yet (shipping cost pending)")}
              </span>
            </Tooltip>
          )}
        </div>
      </div>

      {/* Payment Transactions Section */}
      {transactions && transactions.length > 0 && (
        <div style={{ marginTop: 18 }}>
          <Collapse
            activeKey={activeCollapse}
            onChange={setActiveCollapse}
            ghost
            items={[
              {
                key: "transactions",
                label: (
                  <span
                    style={{
                      fontWeight: 600,
                      color: "#1677ff",
                      fontSize: 13,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    <CreditCardOutlined /> {t("orderDetails.viewTransactions", { count: transactions.length, defaultValue: `View Payment & Refund Transactions (${transactions.length})` })}
                  </span>
                ),
                children: (
                  <Table
                    dataSource={transactions}
                    columns={transactionColumns}
                    rowKey={(rec, idx) => rec.id || idx}
                    pagination={false}
                    size="small"
                    style={{ marginTop: 8 }}
                  />
                ),
              },
            ]}
          />
        </div>
      )}
    </Card>
  );
};

export default OrderSummaryCard;
